import { getArchetypes, getDefaultInputValues } from "@/features/branding";
import { defaultBuildYear } from "@/features/risk_bands";
import { keys, outdatedKeys, PortfolioExportRawRow } from "@/tools/aggregate/portfolio-export/schema";
import { PortfolioExportAssetData } from "@/tools/aggregate/portfolio-export/types";
import { parsePortfolioExportUploadLine } from "@/tools/aggregate/portfolio-export/utils/parse";

export type PortfolioAssetPayload = Omit<PortfolioExportAssetData, "archetype"> & {
  archetype?: string;
};

/**
 * Converts one line of an upload CSV into the analysis payload for that asset.
 *
 * @param line - A single data row from the CSV. Must not be the header.
 * @param header - The CSV's header row, used to name the columns.
 * @param itemId - Pre-generated id for this asset; also emitted as `item_id`.
 * @param regionType - Selects the archetype set and default input values.
 */
export const toPortfolioAsset = (
  line: string,
  header: string,
  itemId: string,
  regionType?: string
): PortfolioAssetPayload => {
  const archetypes = getArchetypes(regionType);
  const parsedLine = parsePortfolioExportUploadLine(line, header);

  const defaultArchetype = archetypes.find(
    ({ value }) => value === "HighRiseFlat" || value === "HighRiseResidentialFlatHK"
  )!;

  // The row satisfies one branch of the location union; reading it as the
  // flattened row type makes every column addressable by key.
  const row = parsedLine as PortfolioExportRawRow;

  /**
   * Reads a column, collapsing `null` to `undefined` so that no null values end up
   * in the request.
   */
  const getValue = <K extends keyof PortfolioExportRawRow>(
    key: K
  ): NonNullable<PortfolioExportRawRow[K]> | undefined => {
    if (key in row) {
      const value = row[key];
      return value === null || value === undefined ? undefined : (value as NonNullable<PortfolioExportRawRow[K]>);
    }

    return undefined;
  };

  // TODO the batch upload process is untested
  const data: PortfolioAssetPayload = {
    item_id: itemId,
  };

  // Store both LatLongs and Address if supplied
  const latitude = getValue(keys.latitude);
  const address = getValue(keys.address);

  if (latitude) {
    data.coordinates = {
      latitude,
      longitude: getValue(keys.longitude),
      elevation: getValue(keys.elevation),
    };
  }

  if (address) {
    data.geocoding = {
      address: getValue(keys.address),
    };
  }

  const archetype = archetypes.find(({ name }) => name === getValue(keys.archetype)) || defaultArchetype;
  data.archetype = archetype.value;

  data.build_year = getValue(keys.buildYear) || archetype.defaultBuildYear || defaultBuildYear;
  data.userSuppliedId = getValue(keys.id)?.toString().trim();

  const providedHeightAboveGround = parsedLine[keys.floorHeight];
  data["thresholds"] = {
    flood: {
      height_above_ground:
        typeof providedHeightAboveGround !== "number"
          ? archetype.defaultHeightAboveGround || 0
          : providedHeightAboveGround,
    },
  };

  const providedHeatThresholdReturnPeriod =
    parsedLine[keys.heatThresholdReturnPeriod] ?? parsedLine[outdatedKeys.heatThresholdReturnPeriod];
  const providedHeatThresholdReturnFrequency = parsedLine[keys.heatThresholdReturnFrequency];
  const providedHeatThresholdTemperature =
    parsedLine[keys.heatThresholdTemperature] ?? parsedLine[outdatedKeys.heatThresholdTemperature];
  if (
    typeof providedHeatThresholdReturnPeriod === "number" ||
    typeof providedHeatThresholdReturnFrequency === "number" ||
    typeof providedHeatThresholdTemperature === "number"
  ) {
    // Validation permits at most one of the three, and `JSON.stringify` drops
    // undefined keys - so emitting only the one that is set is identical on the
    // wire, and matches the exactly-one-variant shape the facades types declare.
    data["thresholds"] = {
      ...data["thresholds"],
      heat:
        typeof providedHeatThresholdTemperature === "number"
          ? { temperature: providedHeatThresholdTemperature }
          : typeof providedHeatThresholdReturnPeriod === "number"
            ? { return_period: providedHeatThresholdReturnPeriod }
            : { return_frequency: providedHeatThresholdReturnFrequency as number },
    };
  }

  const providedWindThresholdReturnPeriod =
    parsedLine[keys.windThresholdReturnPeriod] ?? parsedLine[outdatedKeys.windThresholdReturnPeriod];
  const providedWindThresholdReturnFrequency = parsedLine[keys.windThresholdReturnFrequency];
  if (
    typeof providedWindThresholdReturnPeriod === "number" ||
    typeof providedWindThresholdReturnFrequency === "number"
  ) {
    data["thresholds"] = {
      ...data["thresholds"],
      wind:
        typeof providedWindThresholdReturnPeriod === "number"
          ? { return_period: providedWindThresholdReturnPeriod }
          : { return_frequency: providedWindThresholdReturnFrequency as number },
    };
  }

  const providedTCWThresholdReturnPeriod =
    parsedLine[keys.tcwThresholdReturnPeriod] ?? parsedLine[outdatedKeys.tcwThresholdReturnPeriod];
  const providedTCWThresholdReturnFrequency = parsedLine[keys.tcwThresholdReturnFrequency];
  const providedTCWThresholdWindSpeed =
    parsedLine[keys.tcwThresholdWindSpeed] ?? parsedLine[outdatedKeys.tcwThresholdWindSpeed];
  if (
    typeof providedTCWThresholdWindSpeed === "number" ||
    typeof providedTCWThresholdReturnPeriod === "number" ||
    typeof providedTCWThresholdReturnFrequency === "number"
  ) {
    data["thresholds"] = {
      ...data["thresholds"],
      cyclone_wind:
        typeof providedTCWThresholdWindSpeed === "number"
          ? { wind_speed: providedTCWThresholdWindSpeed }
          : typeof providedTCWThresholdReturnPeriod === "number"
            ? { return_period: providedTCWThresholdReturnPeriod }
            : { return_frequency: providedTCWThresholdReturnFrequency as number },
    };
  }

  const { marketValue, mortgageRate, replacementCost } = getDefaultInputValues(archetype.value, regionType);
  data["financial"] = {
    interest_rate: getValue(keys.mortgageRate) ?? mortgageRate,
    market_value: getValue(keys.marketValue) ?? marketValue,
    replacement_cost: getValue(keys.replacementCost) ?? replacementCost,
  };

  // Only supplied when present in the template; unset fields fall back to
  // the archetype's defaults server-side.
  const fireProtection = getValue(keys.fireProtection);
  const foundationDesign = getValue(keys.foundationDesign);
  if (fireProtection !== undefined || foundationDesign !== undefined) {
    data["protection"] = {
      fire_protection: fireProtection,
      foundation_design: foundationDesign,
    };
  }

  return data;
};
